import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BASE_URL } from "../../_constants/urlConstants";

const CATALOG_BASE = `${BASE_URL}/api/ProjectEquipment/Catalog`;

// Shared by the Model/Code/Rate header and every value row so the columns
// line up exactly — Bootstrap's .row/.col gutter margins don't guarantee that.
const CATALOG_ROW_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "1fr 140px 110px",
  columnGap: "0.5rem",
};

function groupCatalogRows(rows) {
  const classMap = new Map();
  for (const r of rows) {
    if (!classMap.has(r.equipmentClass)) {
      classMap.set(r.equipmentClass, {
        equipmentClass: r.equipmentClass,
        equipmentClassDescription: r.equipmentClassDescription,
        // Free-text note for this class — parallels makeComments, but only meaningful for the
        // Standard (non-Misc) catalog, so it's rendered conditionally on browseType below.
        classComments: r.classComments,
        delayFactor: r.delayFactor,
        otFactor: r.otFactor,
        makes: new Map(),
      });
    }
    const cls = classMap.get(r.equipmentClass);
    if (!cls.makes.has(r.equipmentMake)) {
      cls.makes.set(r.equipmentMake, {
        equipmentMake: r.equipmentMake,
        equipmentMakeDescription: r.equipmentMakeDescription,
        makeComments: r.makeComments,
        // Header label for the Model column — "Model" for most classes, "OVER    TO" for
        // graduated/tiered classes. Taken from this group's first row since it's a
        // per-class/make attribute, not a per-model one.
        modelDisplay: r.modelDisplay || "Model",
        models: [],
      });
    }
    cls.makes.get(r.equipmentMake).models.push(r);
  }
  return Array.from(classMap.values()).map((c) => ({
    ...c,
    makes: Array.from(c.makes.values()),
  }));
}

function filterGroupedCatalog(grouped, filterText) {
  const term = filterText.trim().toLowerCase();
  if (!term) return grouped;
  return grouped
    .map((cls) => {
      const classHit = `${cls.equipmentClass} ${cls.equipmentClassDescription}`
        .toLowerCase()
        .includes(term);
      const makes = cls.makes
        .map((mk) => {
          const makeHit =
            classHit ||
            `${mk.equipmentMake} ${mk.equipmentMakeDescription}`.toLowerCase().includes(term);
          const models = mk.models.filter(
            (m) =>
              makeHit ||
              `${m.equipmentModel} ${m.equipmentModelDescription}`.toLowerCase().includes(term)
          );
          return models.length ? { ...mk, models } : null;
        })
        .filter(Boolean);
      return makes.length ? { ...cls, makes } : null;
    })
    .filter(Boolean);
}

export default function EquipmentCatalogBrowserModal(props) {
  const { isOpen, equipmentType, reportDate, catalogYearLabel, onSelect, onClose } = props;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState("");
  // Defaults to whatever type the parent modal is currently in, but can be flipped
  // within the browser without closing it — the selected row's own type is fed
  // back via onSelect so the parent can switch its entry mode to match.
  const [browseType, setBrowseType] = useState(equipmentType);

  useEffect(() => {
    if (isOpen) setBrowseType(equipmentType);
  }, [isOpen, equipmentType]);

  useEffect(() => {
    if (!isOpen || !browseType || !reportDate) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${CATALOG_BASE}/Browse`, { params: { equipmentType: browseType, reportDate } })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data || []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Unable to load the equipment catalog.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, browseType, reportDate]);

  useEffect(() => {
    if (!isOpen) setFilterText("");
  }, [isOpen]);

  const grouped = useMemo(() => groupCatalogRows(rows), [rows]);
  const visible = useMemo(() => filterGroupedCatalog(grouped, filterText), [grouped, filterText]);

  const isMisc = browseType === "M";
  const modalTitle = isMisc ? "Miscellaneous Rental Rates" : "Equipment Rental Rates";

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl">
      <ModalHeader toggle={onClose} className={isMisc ? "bg-warning" : "bg-info text-white"}>
        <FontAwesomeIcon icon="table" className="fa-fw mr-2" />
        {modalTitle}{catalogYearLabel ? ` — ${catalogYearLabel}` : ""}
      </ModalHeader>
      <ModalBody>
        <div className="btn-group btn-group-sm d-flex w-100 mb-2" role="group">
          {[
            { key: "E", label: "Standard" },
            { key: "M", label: "Miscellaneous" },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`btn ${browseType === key ? "btn-primary" : "btn-outline-secondary"}`}
              style={{ flex: "1 1 0" }}
              onClick={() => setBrowseType(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          type="text"
          className="form-control mb-2"
          placeholder="Filter by class, make, or model..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          autoComplete="off"
        />
        {loading && (
          <div className="text-muted">
            <FontAwesomeIcon icon="spinner" spin className="fa-fw mr-1" />
            Loading catalog...
          </div>
        )}
        {error && <div className="alert alert-danger py-2">{error}</div>}
        {!loading && !error && visible.length === 0 && (
          <div className="text-muted">No matching catalog entries.</div>
        )}
        <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
          {visible.map((cls) => (
            <div className="card mb-3" key={cls.equipmentClass}>
              <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center py-2">
                <span>
                  <b>{cls.equipmentClassDescription}</b>{" "}
                  <span className="badge badge-light ml-1">{cls.equipmentClass}</span>
                </span>
                <small>
                  DELAY FACTOR = {cls.delayFactor} &nbsp;&nbsp; OVERTIME FACTOR = {cls.otFactor}
                </small>
              </div>
              {!isMisc && cls.classComments && (
                <div
                  className="px-2 pt-1 small"
                  style={{ whiteSpace: "pre-wrap", color: "#000" }}
                >
                  {cls.classComments}
                </div>
              )}
              <div className="card-body py-2 bg-light">
                {cls.makes.map((mk) => (
                  <div key={mk.equipmentMake} className="mb-2">
                    <div className="rounded px-2 py-1 mb-1 d-flex align-items-center">
                      <b>{mk.equipmentMakeDescription}</b>{" "}
                      <span className="badge badge-secondary ml-1">{mk.equipmentMake}</span>
                    </div>
                    {mk.makeComments && (
                      <div
                        className="px-2 mb-1 small"
                        style={{ whiteSpace: "pre-wrap", color: "#000" }}
                      >
                        {mk.makeComments}
                      </div>
                    )}
                    <div className="pb-1 mb-1 pl-3 pr-3">
                      <div className="text-secondary" style={CATALOG_ROW_GRID_STYLE}>
                        <div style={{ whiteSpace: "pre-wrap" }}>{mk.modelDisplay}</div>
                        <div>Code</div>
                        <div className="text-right">Rate</div>
                      </div>
                    </div>
                    {mk.models.map((m) => (
                      <div
                        key={m.equipmentModel}
                        className="list-group-item list-group-item-action py-1 pl-3 pr-3 border-1 text-body"
                        style={{ cursor: "pointer" }}
                        onClick={() => onSelect({ ...m, equipmentType: browseType })}
                      >
                        <div style={{ ...CATALOG_ROW_GRID_STYLE, fontFamily: "'Times New Roman', Times, serif" }}>
                          <div style={{ whiteSpace: "pre-wrap" }}>{m.equipmentModelDescription}</div>
                          <div>{m.equipmentModel}</div>
                          <div className="text-right">${Number(m.rate).toFixed(2)}</div>
                        </div>
                        {isMisc && m.modelComments && (
                          <div className="small text-muted" style={{ whiteSpace: "pre-wrap" }}>
                            {m.modelComments}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ModalBody>
    </Modal>
  );
}
